import { useCallback, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  REALADVISOR_ES_TENANT_ID,
  REALADVISOR_FR_TENANT_ID,
  REALADVISOR_IT_TENANT_ID,
  REALADVISOR_PL_TENANT_ID,
  REALADVISOR_TENANT_ID,
} from '@realadvisor/constants';

import {
  Cma_Report_Source_Enum_Enum,
  type Cma_Reports_Insert_Input,
  type Properties_Insert_Input,
} from '@graphql/__generated__/graphql';
import { useDuplicatePropertyData } from '@modules/_shared/hooks/useDuplicatePropertyData';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useGetDefaultApolloTemplate } from '@modules/_shared/utils/template';
import { GET_CMA_REPORTS_LIST } from '@modules/reports/cma-reports/CMAReportsList';
import {
  CREATE_CMA_REPORT,
  DOC_TEMPLATES_CMA_REPORT,
  GET_CMA_REPORTS_COUNT,
} from '@modules/reports/cma-reports/cmaReportsQueries';

type CreateCMAReportInput = {
  leadId: string;
  contactId?: string;
  propertyId?: string;
};

type CreateCMAReportHookResult = [
  ErrorLike | null,
  boolean,
  (input: CreateCMAReportInput) => Promise<string | null>,
  () => void,
];

export const useCreateCMAReport = (): CreateCMAReportHookResult => {
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);

  const [createCMAReportMutation, { loading: createCMAReportLoading }] =
    useMutation(CREATE_CMA_REPORT, {
      refetchQueries: [GET_CMA_REPORTS_LIST, GET_CMA_REPORTS_COUNT],
      onError: setErrorLike,
    });

  const [getDocTemplates, { loading: getDocTemplatesLoading }] = useLazyQuery(
    DOC_TEMPLATES_CMA_REPORT,
    {
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    },
  );

  const [
    getDuplicatePropertyData,
    { loading: getPropertyDuplicationDataLoading },
  ] = useDuplicatePropertyData({ onError: setErrorLike });

  const { getDefaultTemplate } = useGetDefaultApolloTemplate();
  const { me } = useAppData();

  const createCMAReport = useCallback(
    async ({ leadId, contactId, propertyId }: CreateCMAReportInput) => {
      const { data: docTemplatesData } = await getDocTemplates();

      const tenantId = me?.tenant.id;
      const userLanguage = me?.language;
      const language = determineLanguage(tenantId, userLanguage);

      const defaultTemplateId = getDefaultTemplate(
        docTemplatesData?.doc_templates ?? [],
      )?.id;

      let propertyInput: Properties_Insert_Input | null = null;

      if (propertyId == null) {
        propertyInput = await getDuplicatePropertyData(leadId);

        if (propertyInput == null) {
          return null;
        }
      }

      const input: Cma_Reports_Insert_Input = {
        lead_id: leadId,
        doc_template_id: defaultTemplateId,
        contact_id: contactId,
        broker_id: me?.id,
        language,
        property_id: propertyId,
        report_source: Cma_Report_Source_Enum_Enum.CmaFromScratch,
        ...(propertyInput != null ? { property: { data: propertyInput } } : {}),
      };

      const { data, error: cmaReportError } = await createCMAReportMutation({
        variables: { input },
      });

      if (cmaReportError || !data?.insert_cma_reports_one) {
        return null;
      }

      return data.insert_cma_reports_one.id;
    },
    [
      createCMAReportMutation,
      getDefaultTemplate,
      getDocTemplates,
      getDuplicatePropertyData,
      me?.id,
      me?.language,
      me?.tenant.id,
    ],
  );

  return [
    apolloError,
    createCMAReportLoading ||
      getDocTemplatesLoading ||
      getPropertyDuplicationDataLoading,
    createCMAReport,
    () => setErrorLike(null),
  ];
};

export const determineLanguage = (
  tenantId: string | undefined,
  userLanguage: string | undefined,
): string => {
  const defaultLang = userLanguage ?? 'en';

  // Spanish not supported for main RealAdvisor tenant
  if (tenantId === REALADVISOR_TENANT_ID && userLanguage === 'es') {
    return 'en';
  }

  // Country-specific tenants require matching language
  const tenantLanguageMap: Record<string, string> = {
    [REALADVISOR_ES_TENANT_ID]: 'es',
    [REALADVISOR_FR_TENANT_ID]: 'fr',
    [REALADVISOR_IT_TENANT_ID]: 'it',
    [REALADVISOR_PL_TENANT_ID]: 'pl',
  };

  const requiredLang = tenantId ? tenantLanguageMap[tenantId] : undefined;
  if (requiredLang && userLanguage !== requiredLang) {
    return 'en';
  }

  return defaultLang;
};
